import { useState } from 'react'
import { Image as ImageIcon, Plus, LayoutGrid } from 'lucide-react'
import { Modal } from '../ui/Modal'
import { LoadingSpinner } from '../ui/LoadingSpinner'
import { useContentImages } from '../../hooks/useVisualTasks'
import { useVisualTasks } from '../../hooks/useVisualTasks'
import { useAppStore } from '../../stores/app'
import { useQueryClient } from '@tanstack/react-query'
import { ImageGallery } from '../visual/ImageGallery'
import { VisualTaskForm } from '../visual/VisualTaskForm'
import { EditImageModal } from '../visual/EditImageModal'
import { getImageAssetUrl } from '../../lib/api'
import type { GeneratedImage } from '../../lib/api'

interface ContentImagesPanelProps {
  contentId: string | null
  contentExcerpt: string
  channel?: string
}

export function ContentImagesPanel({
  contentId,
  contentExcerpt,
}: ContentImagesPanelProps) {
  const organizationId = useAppStore((s) => s.organizationId)
  const queryClient = useQueryClient()
  const { data: images = [], isLoading } = useContentImages(contentId)
  const { createTask, isCreating } = useVisualTasks()

  const [generateModalOpen, setGenerateModalOpen] = useState(false)
  const [galleryModalOpen, setGalleryModalOpen] = useState(false)
  const [imageToEdit, setImageToEdit] = useState<GeneratedImage | null>(null)
  const [useAsReferenceImage, setUseAsReferenceImage] = useState<GeneratedImage | null>(null)

  const hasImages = images.length > 0

  const handleGenerateSuccess = async (data: Parameters<typeof createTask>[0]) => {
    await createTask(data)
    if (contentId) {
      queryClient.invalidateQueries({ queryKey: ['content_images', contentId] })
    }
    setGenerateModalOpen(false)
    setUseAsReferenceImage(null)
  }

  const openGenerateWithReference = (image: GeneratedImage) => {
    setUseAsReferenceImage(image)
    setGenerateModalOpen(true)
    setGalleryModalOpen(false)
  }

  const handleEditSuccess = () => {
    if (contentId) {
      queryClient.invalidateQueries({ queryKey: ['content_images', contentId] })
    }
    setImageToEdit(null)
  }

  if (!contentId) return null

  return (
    <>
      <div>
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
          Imagenes
        </h3>
        {isLoading ? (
          <div className="flex justify-center py-4">
            <LoadingSpinner size="sm" />
          </div>
        ) : (
          <div className="space-y-3">
            {hasImages && (
              <div className="grid grid-cols-4 gap-1.5">
                {images.slice(0, 8).map((img) => (
                  <button
                    key={img.id}
                    type="button"
                    onClick={() => setGalleryModalOpen(true)}
                    className="aspect-square rounded border border-slate-200 overflow-hidden hover:ring-2 hover:ring-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <img
                      src={getImageAssetUrl(img.id)}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        if (img.public_url) {
                          ;(e.target as HTMLImageElement).src = img.public_url
                        }
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setUseAsReferenceImage(null)
                  setGenerateModalOpen(true)
                }}
                className="btn btn-primary text-sm flex items-center gap-1.5 py-1.5 px-3"
              >
                <Plus className="w-3.5 h-3.5" />
                Generar imagen
              </button>
              {hasImages && (
                <button
                  type="button"
                  onClick={() => setGalleryModalOpen(true)}
                  className="btn btn-secondary text-sm flex items-center gap-1.5 py-1.5 px-3"
                >
                  <LayoutGrid className="w-3.5 h-3.5" />
                  Ver todas ({images.length})
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Generate image modal */}
      <Modal
        isOpen={generateModalOpen}
        onClose={() => {
          setGenerateModalOpen(false)
          setUseAsReferenceImage(null)
        }}
        title={useAsReferenceImage ? 'Generar desde referencia' : 'Generar imagen para esta pieza'}
        size="lg"
      >
        <VisualTaskForm
          contentId={contentId}
          initialContentExcerpt={
            useAsReferenceImage?.prompt_used
              ? `Basado en: ${useAsReferenceImage.prompt_used.slice(0, 200)}...`
              : contentExcerpt.slice(0, 1000)
          }
          sourceImageId={useAsReferenceImage?.id ?? null}
          onSubmit={handleGenerateSuccess}
          isLoading={isCreating}
        />
      </Modal>

      {/* Full gallery modal */}
      <Modal
        isOpen={galleryModalOpen}
        onClose={() => setGalleryModalOpen(false)}
        title="Imagenes generadas"
        size="xl"
      >
        {hasImages ? (
          <>
            <ImageGallery
              images={images}
              onRequestEdit={organizationId ? (img: GeneratedImage) => setImageToEdit(img) : undefined}
              onUseAsReference={organizationId ? openGenerateWithReference : undefined}
            />
            {imageToEdit && (
              <EditImageModal
                isOpen={true}
                onClose={() => setImageToEdit(null)}
                image={imageToEdit}
                contentId={contentId}
                onSuccess={handleEditSuccess}
              />
            )}
          </>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <ImageIcon className="w-10 h-10 mx-auto mb-2 text-slate-400" />
            <p className="text-sm">No hay imagenes generadas aun</p>
          </div>
        )}
      </Modal>
    </>
  )
}
